import { motion } from "framer-motion";
import { useRef, useState } from "react";
import { useFontStyle } from "~/hook/useFontStyle";
import { cn } from "~/lib/utils";

interface TabDef {
  key: string;
  label: string;
}
interface TabComponentProps {
  tabs: TabDef[];
  selectedTab: string;
  onTabChange: (tab: string) => void;
}

interface TabProps {
  tab: TabDef;
  currentTab: string;
  setSelectedTab: (tab: string) => void;
  setPosition: (position: { left: number; width: number; opacity: number }) => void;
  isHovered: boolean;
}

export const TabComponent = ({ tabs, selectedTab, onTabChange }: TabComponentProps) => {
  return (
    <div className="w-full">
      <SlideTabs tabs={tabs} selectedTab={selectedTab} setSelectedTab={onTabChange} />
    </div>
  );
};

const SlideTabs = ({ tabs, selectedTab, setSelectedTab }: { tabs: TabDef[]; selectedTab: string; setSelectedTab: (tab: string) => void }) => {
  const [position, setPosition] = useState({
    left: 0,
    width: 0,
    opacity: 0,
  });
  const [isHovered, setIsHovered] = useState(false);

  return (
    <ul
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setPosition((prev) => ({
          ...prev,
          opacity: 0,
        }));
        setIsHovered(false);
      }}
      className="relative mx-auto flex w-fit rounded-full bg-secondaryColor p-1"
    >
      {tabs.map((tab) => (
        <Tab key={tab.key} tab={tab} currentTab={selectedTab} setSelectedTab={setSelectedTab} setPosition={setPosition} isHovered={isHovered} />
      ))}

      <Cursor position={position} />
    </ul>
  );
};

const Tab = ({ tab, currentTab, setSelectedTab, setPosition, isHovered }: TabProps) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const isActive = currentTab === tab.key;
  const { medium } = useFontStyle();
  return (
    <button
      ref={ref}
      onMouseEnter={() => {
        if (!ref?.current) return;
        const { width } = ref.current.getBoundingClientRect();
        setPosition({
          left: ref.current.offsetLeft,
          width,
          opacity: 1,
        });
      }}
      onClick={() => setSelectedTab(tab.key)}
      className={cn(
        "relative z-10 block cursor-pointer transition-colors duration-200",
        "px-3 py-2  md:px-5 md:py-3 body_text_m",
        "capitalize tracking-wide rounded-full",
        isActive && !isHovered ? "text-white bg-buttonColor" : "text-textColor hover:text-white",
        medium,
      )}
    >
      {tab.label}
    </button>
  );
};

const Cursor = ({ position }: { position: { left: number; width: number; opacity: number } }) => {
  return (
    <motion.li
      animate={{
        ...position,
      }}
      className="absolute h-9 rounded-full bg-buttonColor md:h-[50px]"
    />
  );
};

export default TabComponent;
